/**
 * Connector shelf. The "Connect" panel.
 *
 * Twelve govt + identity connectors. Each row is one connector with
 * status badge, primary CTA (Import / Reconnect), secondary CTA (Disconnect).
 * On click, the orchestrator routes the payload into the vault.
 *
 * Calm-tech: nothing imports without an explicit tap.
 *
 * Mode: every connector starts in 'test' (returns realistic mock data).
 * Wire up live mode by setting globalThis.__EMPIRE_CONNECTORS_DEV__ = true
 * before bootstrap (e.g. in a settings panel) and providing the relevant
 * partner config to the adapter factory.
 */

import { injectCommonStyles, withVault } from './_shared.ts';
import { makeDigilockerConnector } from '../chassis/connectors/govt/digilocker.ts';
import { makeAadhaarConnector } from '../chassis/connectors/govt/aadhaar-offline-kyc.ts';
import { makeMparivahanConnector } from '../chassis/connectors/govt/mparivahan.ts';
import { makeItrImporter } from '../chassis/connectors/govt/itr-importer.ts';
import { makePassportParser } from '../chassis/connectors/govt/passport-parser.ts';
import { makeCowinVaxCert } from '../chassis/connectors/govt/cowin-vax-cert.ts';
import { makeSroPropertyConnector } from '../chassis/connectors/govt/sro-property.ts';
import { makeIrctcHistory } from '../chassis/connectors/govt/irctc-history.ts';
import { makeAbdmPhrStub } from '../chassis/connectors/govt/abdm-phr-stub.ts';
import { makeEsignConnector } from '../chassis/connectors/govt/esign-nsdl.ts';
import { makeUmangStub } from '../chassis/connectors/govt/umang-stub.ts';
import { validatePan, maskPan } from '../chassis/connectors/govt/pan-validator.ts';
import {
  ingestAadhaar,
  ingestCowin,
  ingestDigilocker,
  ingestIrctc,
  ingestItr,
  ingestMparivahan,
  ingestPassport,
  ingestSroProperty,
  type OrchestratorReport,
} from '../chassis/connector-orchestrator.ts';

interface ConnectorRow {
  readonly id: string;
  readonly title: string;
  readonly titleHi: string;
  readonly subtitle: string;
  readonly category: 'identity' | 'wealth' | 'health' | 'travel' | 'discovery';
  readonly primaryLabel: string;
  primaryAction: () => Promise<OrchestratorReport | string>;
  secondaryLabel: string;
  secondaryAction?: () => Promise<void>;
}

const TPL = /* html */ `
<style>
.shelf-list { display: grid; gap: 10px; margin-top: 6px; }
.row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 12px 14px;
  background: var(--parchment);
  border: 1px solid var(--rule-hair);
  border-left: 2px solid var(--indigo);
  border-radius: 2px;
}
.row.identity { border-left-color: var(--indigo); }
.row.wealth { border-left-color: var(--saffron-deep); }
.row.health { border-left-color: var(--leaf); }
.row.travel { border-left-color: var(--ink); }
.row.discovery { border-left-color: var(--ink-faint); }
.title-line {
  font-family: var(--display);
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.title-line .deva { font-family: var(--hindi); font-weight: 400; opacity: 0.75; font-size: 14px; }
.subtitle { font-family: var(--mono); font-size: 10px; color: var(--ink-faint); letter-spacing: 0.06em; margin-top: 2px; }
.row .controls {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}
.status-pill {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 99px;
  border: 1px solid var(--rule-hair);
  color: var(--ink-dim);
  background: var(--parchment-warm);
}
.status-pill.connected { color: var(--leaf); border-color: oklch(48% 0.110 145 / 0.35); background: oklch(48% 0.110 145 / 0.08); }
.status-pill.live { color: var(--saffron-deep); border-color: oklch(58% 0.140 72 / 0.40); background: oklch(70% 0.150 78 / 0.12); }
.report-strip {
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px dashed var(--rule-strong);
  border-radius: 2px;
  font-family: var(--mono);
  font-size: 10.5px;
  letter-spacing: 0.06em;
  color: var(--indigo);
  background: var(--indigo-wash);
  min-height: 24px;
  white-space: pre-line;
}
.pan-tool {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--rule-hair);
}
.pan-tool input { width: 100%; }
.pan-tool .out { font-family: var(--mono); font-size: 11px; color: var(--ink-dim); padding: 6px 0; }
.pan-tool .out.valid { color: var(--leaf); }
.pan-tool .out.structural { color: var(--saffron-deep); }
.pan-tool .out.invalid { color: var(--crimson); }
.pull-all {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--rule-hair);
}
</style>
<section class="frame">
  <h2>Connect <span class="deva">जुड़िए</span></h2>
  <div class="kicker">Govt sources · DigiLocker · Aadhaar · ITR · mParivahan · CoWIN · eSign</div>
  <div class="shelf-list" id="rows"></div>

  <div class="pull-all">
    <div class="btn-row">
      <button class="btn" id="pull-all">Pull all govt docs</button>
      <button class="btn ghost" id="release-modal-test">Test eSign mock signer</button>
    </div>
    <div class="report-strip" id="report">Tap a row to import test-mode data into your vault. Nothing leaves the device.</div>
  </div>

  <div class="pan-tool">
    <label class="field" style="grid-column: 1 / -1;">PAN structural check
      <input id="pan-input" placeholder="ABCDE1234F" maxlength="10" />
    </label>
    <div class="out" id="pan-out" style="grid-column: 1 / -1;">Type a PAN to validate it locally.</div>
  </div>
</section>
`;

export class WasiyatConnectorShelf extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;
  #digilocker = makeDigilockerConnector('test');
  #aadhaar = makeAadhaarConnector('test');
  #mparivahan = makeMparivahanConnector('test');
  #itr = makeItrImporter('test');
  #passport = makePassportParser('test');
  #cowin = makeCowinVaxCert('test');
  #sro = makeSroPropertyConnector('test');
  #irctc = makeIrctcHistory('test');
  #abdm = makeAbdmPhrStub('test');
  #esign = makeEsignConnector('test');
  #umang = makeUmangStub('test');

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    const rowsEl = this.#shadow.getElementById('rows');
    const reportEl = this.#shadow.getElementById('report') as HTMLDivElement;
    if (!rowsEl) return;
    this.#renderRows(rowsEl, reportEl);
    this.#wirePullAll(reportEl);
    this.#wirePanInput();
    this.#wireEsignTest(reportEl);
    // Refresh on every vault update so connected badges stay current.
    this.#unsub = withVault(this, () => void this.#refreshStatuses());
    void this.#refreshStatuses();
  }

  disconnectedCallback(): void {
    this.#unsub?.();
  }

  #renderRows(parent: HTMLElement, reportEl: HTMLDivElement): void {
    const rows: ConnectorRow[] = this.#rows(reportEl);
    parent.innerHTML = rows.map((r) => `
      <div class="row ${r.category}" data-id="${r.id}">
        <div>
          <div class="title-line">${escape(r.title)} <span class="deva">${escape(r.titleHi)}</span></div>
          <div class="subtitle">${escape(r.subtitle)}</div>
        </div>
        <div class="controls">
          <span class="status-pill" data-status="${r.id}">disconnected</span>
          <div class="btn-row">
            <button class="btn tiny" data-action="primary" data-id="${r.id}">${escape(r.primaryLabel)}</button>
            ${r.secondaryAction ? `<button class="btn tiny ghost" data-action="secondary" data-id="${r.id}">${escape(r.secondaryLabel)}</button>` : ''}
          </div>
        </div>
      </div>
    `).join('');
    parent.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset['id'];
        const row = rows.find((r) => r.id === id);
        if (!row) return;
        const action = btn.dataset['action'] === 'primary' ? row.primaryAction : row.secondaryAction;
        if (!action) return;
        btn.disabled = true;
        reportEl.textContent = `${row.title} · running…`;
        try {
          const result = await action();
          if (result === undefined) {
            reportEl.textContent = `${row.title} · cleared`;
          } else if (typeof result === 'string') {
            reportEl.textContent = `${row.title} · ${result}`;
          } else {
            reportEl.textContent = `${row.title} · ${reportToText(result)}`;
          }
        } catch (e) {
          reportEl.textContent = `${row.title} · failed · ${e instanceof Error ? e.message : 'unknown'}`;
        } finally {
          btn.disabled = false;
          void this.#refreshStatuses();
        }
      });
    });
  }

  #rows(reportEl: HTMLDivElement): ConnectorRow[] {
    return [
      {
        id: 'digilocker', title: 'DigiLocker', titleHi: 'डिजिलॉकर',
        subtitle: 'PAN · DL · Voter ID · RC · marksheets · auto-create asset rows',
        category: 'identity',
        primaryLabel: 'Pull',
        primaryAction: async () => {
          const res = await this.#digilocker.importPayload();
          if (!res.ok) return res.error.message;
          return ingestDigilocker(res.value);
        },
        secondaryLabel: 'Disconnect',
        secondaryAction: () => this.#digilocker.disconnect(),
      },
      {
        id: 'aadhaar-offline-kyc', title: 'Aadhaar offline e-KYC', titleHi: 'आधार ई-केवाईसी',
        subtitle: 'Share-code zip · RSA-2026 sig verify · last4 binds to nominee',
        category: 'identity',
        primaryLabel: 'Import',
        primaryAction: async () => {
          const res = await this.#aadhaar.importPayload();
          if (res.ok) return ingestAadhaar(res.value);
          // No share-code-zip wired yet: use mock for v0
          const mock = this.#aadhaar.mockPayload();
          return ingestAadhaar(mock);
        },
        secondaryLabel: 'Forget',
        secondaryAction: () => this.#aadhaar.disconnect(),
      },
      {
        id: 'itr-importer', title: 'ITR · Schedule AL', titleHi: 'आईटीआर',
        subtitle: 'Schedule AL → bank · MF · demat · jewellery · property rows',
        category: 'wealth',
        primaryLabel: 'Import',
        primaryAction: async () => {
          const res = await this.#itr.importPayload();
          if (!res.ok) return res.error.message;
          return ingestItr(res.value);
        },
        secondaryLabel: 'Disconnect',
        secondaryAction: () => this.#itr.disconnect(),
      },
      {
        id: 'mparivahan', title: 'mParivahan · vehicles', titleHi: 'एम-परिवहन',
        subtitle: 'RC + fastag · creates motor-insurance asset',
        category: 'wealth',
        primaryLabel: 'Import',
        primaryAction: async () => {
          const res = await this.#mparivahan.importPayload();
          if (!res.ok) return res.error.message;
          return ingestMparivahan(res.value);
        },
        secondaryLabel: 'Disconnect',
        secondaryAction: () => this.#mparivahan.disconnect(),
      },
      {
        id: 'sro-property', title: 'Sale-deed parser', titleHi: 'विक्रय पत्र',
        subtitle: 'MH · KA · DL · TN · UP · property + property-deed entry',
        category: 'wealth',
        primaryLabel: 'Demo deed',
        primaryAction: async () => {
          const res = await this.#sro.importPayload();
          if (!res.ok) return res.error.message;
          return ingestSroProperty(res.value);
        },
        secondaryLabel: 'Disconnect',
        secondaryAction: () => this.#sro.disconnect(),
      },
      {
        id: 'passport-parser', title: 'Passport · TD3 MRZ', titleHi: 'पासपोर्ट',
        subtitle: 'Adobe PDF MCP · MRZ check digits · passport doc entry',
        category: 'identity',
        primaryLabel: 'Import',
        primaryAction: async () => {
          const res = await this.#passport.importPayload();
          if (!res.ok) return res.error.message;
          return ingestPassport(res.value);
        },
        secondaryLabel: 'Disconnect',
        secondaryAction: () => this.#passport.disconnect(),
      },
      {
        id: 'cowin-vax-cert', title: 'CoWIN vaccinations', titleHi: 'कोविन',
        subtitle: 'CoWIN cert · medical-record doc entries per dose',
        category: 'health',
        primaryLabel: 'Import',
        primaryAction: async () => {
          const res = await this.#cowin.importPayload();
          if (!res.ok) return res.error.message;
          return ingestCowin(res.value);
        },
        secondaryLabel: 'Disconnect',
        secondaryAction: () => this.#cowin.disconnect(),
      },
      {
        id: 'irctc-history', title: 'IRCTC bookings', titleHi: 'आईआरसीटीसी',
        subtitle: 'IRCTC CSV · travel-history index for family',
        category: 'travel',
        primaryLabel: 'Import',
        primaryAction: async () => {
          const res = await this.#irctc.importPayload();
          if (!res.ok) return res.error.message;
          return ingestIrctc(res.value);
        },
        secondaryLabel: 'Disconnect',
        secondaryAction: () => this.#irctc.disconnect(),
      },
      {
        id: 'abdm-phr-stub', title: 'ABHA · ABDM', titleHi: 'आभा',
        subtitle: '14-digit / address validator · live wiring in Sehat',
        category: 'health',
        primaryLabel: 'Validate',
        primaryAction: async () => {
          const queued = prompt('Enter ABHA id (14 digits or address):');
          if (!queued) return 'cancelled';
          this.#abdm.queueAbha(queued);
          const res = await this.#abdm.importPayload();
          if (!res.ok) return res.error.message;
          reportEl.textContent = '';
          return `${res.value.status} · ${res.value.normalised}`;
        },
        secondaryLabel: 'Clear',
        secondaryAction: () => this.#abdm.disconnect(),
      },
      {
        id: 'umang-stub', title: 'UMANG services', titleHi: 'उमंग',
        subtitle: 'EPFO · NPS · PMJAY · India Post · deep-link discovery',
        category: 'discovery',
        primaryLabel: 'View',
        primaryAction: async () => {
          const snap = (await this.#umang.importPayload()).ok
            ? this.#umang.mockPayload()
            : this.#umang.mockPayload();
          return `${snap.services.length} services queued for v1.1`;
        },
        secondaryLabel: 'Clear',
        secondaryAction: () => this.#umang.disconnect(),
      },
    ];
  }

  #wirePullAll(reportEl: HTMLDivElement): void {
    const btn = this.#shadow.getElementById('pull-all') as HTMLButtonElement;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      reportEl.textContent = 'pulling DigiLocker → ITR → mParivahan → CoWIN → passport → property…';
      const reports: OrchestratorReport[] = [];
      try {
        const dl = await this.#digilocker.importPayload();
        if (dl.ok) reports.push(await ingestDigilocker(dl.value));
        const itr = await this.#itr.importPayload();
        if (itr.ok) reports.push(await ingestItr(itr.value));
        const mp = await this.#mparivahan.importPayload();
        if (mp.ok) reports.push(await ingestMparivahan(mp.value));
        const cw = await this.#cowin.importPayload();
        if (cw.ok) reports.push(await ingestCowin(cw.value));
        const pp = await this.#passport.importPayload();
        if (pp.ok) reports.push(await ingestPassport(pp.value));
        const sro = await this.#sro.importPayload();
        if (sro.ok) reports.push(await ingestSroProperty(sro.value));
        const mock = this.#aadhaar.mockPayload();
        reports.push(await ingestAadhaar(mock));
        reportEl.textContent = reports.map((r) => `${r.source} · ${reportToText(r)}`).join('\n');
      } catch (e) {
        reportEl.textContent = `pull-all failed · ${e instanceof Error ? e.message : 'unknown'}`;
      } finally {
        btn.disabled = false;
        void this.#refreshStatuses();
      }
    });
  }

  #wirePanInput(): void {
    const input = this.#shadow.getElementById('pan-input') as HTMLInputElement;
    const out = this.#shadow.getElementById('pan-out') as HTMLDivElement;
    input.addEventListener('input', () => {
      const raw = input.value;
      if (raw.length < 3) {
        out.className = 'out';
        out.textContent = 'Type a PAN to validate it locally.';
        return;
      }
      const info = validatePan(raw);
      out.className = `out ${info.status}`;
      if (info.status === 'invalid') {
        out.textContent = 'PAN structure invalid. Expected AAAAA9999A.';
        return;
      }
      out.textContent = `${maskPan(info.normalised)} · ${info.holderLabelEn} (${info.holderLabelHi}) · ${info.status}`;
    });
  }

  #wireEsignTest(reportEl: HTMLDivElement): void {
    const btn = this.#shadow.getElementById('release-modal-test') as HTMLButtonElement;
    btn.addEventListener('click', async () => {
      this.#esign.prepare({
        pdfBytes: new TextEncoder().encode('%PDF-1.4\n%%EOF\n'),
        signerName: 'Aditya Sharma',
        signerAadhaarLast4: '4321',
        purpose: 'will',
        reasonShort: 'Wasiyat handover signature test',
      });
      const res = await this.#esign.importPayload();
      if (!res.ok) {
        reportEl.textContent = `eSign mock failed · ${res.error.message}`;
        return;
      }
      reportEl.textContent = `eSign mock receipt · ${res.value.providerCode} · cert ${res.value.certificateSerial} · ${res.value.signedHashSha256B64.slice(0, 12)}…`;
    });
  }

  async #refreshStatuses(): Promise<void> {
    const pairs: { id: string; status: { connected: boolean; mode: 'live' | 'test' } }[] = [];
    pairs.push({ id: 'digilocker', status: pluck(await this.#digilocker.getStatus()) });
    pairs.push({ id: 'aadhaar-offline-kyc', status: pluck(await this.#aadhaar.getStatus()) });
    pairs.push({ id: 'itr-importer', status: pluck(await this.#itr.getStatus()) });
    pairs.push({ id: 'mparivahan', status: pluck(await this.#mparivahan.getStatus()) });
    pairs.push({ id: 'sro-property', status: pluck(await this.#sro.getStatus()) });
    pairs.push({ id: 'passport-parser', status: pluck(await this.#passport.getStatus()) });
    pairs.push({ id: 'cowin-vax-cert', status: pluck(await this.#cowin.getStatus()) });
    pairs.push({ id: 'irctc-history', status: pluck(await this.#irctc.getStatus()) });
    pairs.push({ id: 'abdm-phr-stub', status: pluck(await this.#abdm.getStatus()) });
    pairs.push({ id: 'umang-stub', status: pluck(await this.#umang.getStatus()) });
    for (const p of pairs) {
      const el = this.#shadow.querySelector(`[data-status="${p.id}"]`);
      if (!el) continue;
      el.classList.remove('connected', 'live');
      if (p.status.connected) el.classList.add('connected');
      if (p.status.mode === 'live') el.classList.add('live');
      el.textContent = p.status.connected ? (p.status.mode === 'live' ? 'live' : 'connected') : 'disconnected';
    }
  }
}

function pluck(status: { connected: boolean; mode: 'live' | 'test' }): { connected: boolean; mode: 'live' | 'test' } {
  return { connected: status.connected, mode: status.mode };
}

function reportToText(r: OrchestratorReport): string {
  const bits: string[] = [];
  if (r.assetsCreated) bits.push(`+${r.assetsCreated} asset${r.assetsCreated === 1 ? '' : 's'}`);
  if (r.documentsCreated) bits.push(`+${r.documentsCreated} doc${r.documentsCreated === 1 ? '' : 's'}`);
  if (r.nomineesTouched) bits.push(`${r.nomineesTouched} nominee touched`);
  if (r.skipped.length) bits.push(`${r.skipped.length} skipped`);
  if (bits.length === 0) bits.push('no change');
  return bits.join(' · ');
}

function escape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

customElements.define('wasiyat-connector-shelf', WasiyatConnectorShelf);
